import { phanMuc } from './canhbao';
/**
 * Định dạng hiển thị — TOÀN BỘ mốc thời gian hiện theo giờ Việt Nam (Asia/Ho_Chi_Minh),
 * bất kể máy người dùng đang đặt múi giờ nào. Kỹ thuật viên đọc giờ đóng cắt sai
 * một tiếng là đi lại vô ích cả buổi.
 */

export const TZ = 'Asia/Ho_Chi_Minh';

const dtf = (opts: Intl.DateTimeFormatOptions) => new Intl.DateTimeFormat('vi-VN', { timeZone: TZ, ...opts });

const fDateTime = dtf({
  day: '2-digit', month: '2-digit', year: 'numeric',
  hour: '2-digit', minute: '2-digit', hour12: false,
});
const fDateTimeSec = dtf({
  day: '2-digit', month: '2-digit', year: 'numeric',
  hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
});
const fDate = dtf({ day: '2-digit', month: '2-digit', year: 'numeric' });
const fTime = dtf({ hour: '2-digit', minute: '2-digit', hour12: false });
const fTimeSec = dtf({ hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });

function toDate(v: string | number | Date | null | undefined): Date | null {
  if (v === null || v === undefined || v === '') return null;
  const d = v instanceof Date ? v : new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function fmtDateTime(v: string | number | Date | null | undefined, fallback = '—'): string {
  const d = toDate(v);
  return d ? fDateTime.format(d) : fallback;
}

export function fmtDateTimeSec(v: string | number | Date | null | undefined, fallback = '—'): string {
  const d = toDate(v);
  return d ? fDateTimeSec.format(d) : fallback;
}

export function fmtDate(v: string | number | Date | null | undefined, fallback = '—'): string {
  const d = toDate(v);
  return d ? fDate.format(d) : fallback;
}

export function fmtTime(v: string | number | Date | null | undefined, fallback = '—'): string {
  const d = toDate(v);
  return d ? fTime.format(d) : fallback;
}

export function fmtTimeSec(v: string | number | Date | null | undefined, fallback = '—'): string {
  const d = toDate(v);
  return d ? fTimeSec.format(d) : fallback;
}

/** "3 phút trước", "vừa xong"… dùng cho cột "thấy lần cuối". */
export function fmtAgo(v: string | number | Date | null | undefined, fallback = 'chưa có'): string {
  const d = toDate(v);
  if (!d) return fallback;
  const sec = Math.round((Date.now() - d.getTime()) / 1000);
  if (sec < 0) return 'vừa xong';
  if (sec < 10) return 'vừa xong';
  if (sec < 60) return `${sec} giây trước`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} phút trước`;
  const hour = Math.floor(min / 60);
  if (hour < 24) return `${hour} giờ trước`;
  const day = Math.floor(hour / 24);
  if (day < 30) return `${day} ngày trước`;
  return fmtDate(d);
}

/* --------------------------------------------------------- giờ dạng phút */

/** 1290 → "21:30" */
export function minToStr(min: number): string {
  const m = ((Math.round(min) % 1440) + 1440) % 1440;
  return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
}

/** "21:30" → 1290. Chuỗi hỏng thì trả null để ô nhập tự giữ nguyên. */
export function strToMin(s: string): number | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(s.trim());
  if (!m) return null;
  const h = Number(m[1]);
  const mi = Number(m[2]);
  if (h > 23 || mi > 59) return null;
  return h * 60 + mi;
}

/** Độ dài khoảng bật, có tính trường hợp vắt qua nửa đêm (22:00 → 05:00). */
export function durationMin(onMin: number, offMin: number): number {
  return offMin >= onMin ? offMin - onMin : 1440 - onMin + offMin;
}

export function fmtDuration(min: number): string {
  const h = Math.floor(min / 60);
  const m = min % 60;
  if (h === 0) return `${m} phút`;
  if (m === 0) return `${h} giờ`;
  return `${h} giờ ${m} phút`;
}

/* --------------------------------------------------- thứ trong tuần */

/** Đúng thứ tự firmware: index 0 = Chủ nhật … index 6 = Thứ 7 */
export const DAY_LABELS = ['CN', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7'] as const;
export const DAY_LABELS_FULL = [
  'Chủ nhật', 'Thứ hai', 'Thứ ba', 'Thứ tư', 'Thứ năm', 'Thứ sáu', 'Thứ bảy',
] as const;

export const ALL_DAYS = '1111111';
export const NO_DAYS = '0000000';

/** Mô tả chuỗi 7 ký tự thành câu tiếng Việt gọn. */
export function describeDays(dow: string): string {
  if (!/^[01]{7}$/.test(dow)) return 'không hợp lệ';
  if (dow === ALL_DAYS) return 'Cả tuần';
  if (dow === NO_DAYS) return 'Không ngày nào';
  if (dow === '0111110') return 'Thứ 2 → Thứ 6';
  if (dow === '1000001') return 'Cuối tuần';
  return dow
    .split('')
    .map((c, i) => (c === '1' ? DAY_LABELS[i] : null))
    .filter(Boolean)
    .join(', ');
}

export function toggleDay(dow: string, index: number): string {
  const arr = dow.padEnd(7, '0').slice(0, 7).split('');
  arr[index] = arr[index] === '1' ? '0' : '1';
  return arr.join('');
}

/* ------------------------------------------------------------------ số */

export function fmtNum(v: number | null | undefined, digits = 1, fallback = '—'): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return fallback;
  return v.toLocaleString('vi-VN', { minimumFractionDigits: digits, maximumFractionDigits: digits });
}

export function fmtInt(v: number | null | undefined, fallback = '—'): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return fallback;
  return Math.round(v).toLocaleString('vi-VN');
}

/** Ngày đặc biệt {d,m} → "20/11". d=0 hoặc m=0 nghĩa là chưa đặt. */
export function fmtSpecialDay(sd: { d: number; m: number }): string {
  const d = sd.d ? String(sd.d).padStart(2, '0') : '--';
  const m = sd.m ? String(sd.m).padStart(2, '0') : '--';
  return `${d}/${m}`;
}

/* --------------------------------------------------- nhãn cảnh báo (YC-20) */

/**
 * Mã cảnh báo → câu tiếng Việt cho người vận hành.
 *
 * Bốn mã đầu là thứ tủ gửi lên qua `ExceptionMessage` mã 9001–9005 (YC-10 vòng 2, YC-12).
 * Trước đây màn hình in thẳng `OVER_VOLTAGE` — đúng nhưng người trực đêm đọc không ra, mà
 * họ mới là người phải quyết định có đi hiện trường hay không.
 *
 * ⚠ Vẫn hiện MÃ GỐC bên cạnh, cỡ nhỏ: người vận hành đọc câu tiếng Việt, người sửa lỗi
 * cần đúng chuỗi để tìm trong log và trong `canhbao.ts`.
 */

/*
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * ⛔ BẢNG NHÃN THỨ HAI ĐÃ BỊ XOÁ — 07/09/2026, sau khi lee nói *"ngôn ngữ mô tả lỗi khó hiểu"*.
 *
 * 🔴 Đo được, không phải chuyện gu: **có HAI bảng cùng dịch một mã ra tiếng Việt**, và chúng
 *   bất đồng ở SÁU mã. Tệ hơn, cả hai cùng hiện trên MỘT màn hình:
 *
 *     `phanMuc().nhan` (`canhbao.ts`) → dải Nguy · popup · danh sách ở thanh bên
 *     `nhanCanhBao()`  (bảng ở đây)   → bảng cảnh báo trong trang chi tiết tủ
 *
 *   Sáu chỗ lệch: `METER_TIMEOUT` *"Mất số liệu công tơ"* ↔ *"Không đọc được công tơ"* ·
 *   `RTC_INVALID` *"không tin được"* ↔ *"sai"* · `NVS_ERROR` *"đang chạy lịch MẶC ĐỊNH"* ↔
 *   *"Lỗi bộ nhớ lưu cấu hình"* · `GPS_LOST` *"định vị"* ↔ *"tín hiệu"* · `CONTACTOR_FAIL` ·
 *   `SCHEDULE_MISMATCH`.
 *
 * ⇒ Cùng một cảnh báo đọc ra hai câu khác nhau tuỳ chỗ nhìn. Đó KHÔNG phải "khó hiểu vì viết
 *   dở" — đó là **hai cái tên cho một thứ**, đúng L67. Một bảng, một nguồn.
 * ═══════════════════════════════════════════════════════════════════════════════════════
 */
export function nhanCanhBao(code: string): string {
  return phanMuc(code).nhan;
}
